import { GroupShapeIcon, type GroupShape } from "@/components/shapes/GroupShapeIcon";
import { groupColors } from "@/configs/map/groups";
import { useTranslation } from "@/hooks";
import { useBookmarksContext } from "@/stores/bookmark";
import {
  useActiveDataflow,
  useActiveDataflowId,
  useDataflowStore,
} from "@/stores/dataflow";
import { useMapStore } from "@/stores/map";
import { useUserFiles } from "@/stores/user-file";
import type { Bookmark } from "@/types/europafacts";
import type { UserFile } from "@/types/workspace";
import { PROCESSED_LAYER_PREFIX } from "@/utils/constants/map";
import { getInputDataNameFromFlow } from "@/utils/data/getInputDataName";
import { logger } from "@/utils/debug";
import {
  Button,
  Checkbox,
  ColorInput,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMemo } from "react";

export function WorkspaceModalMapData({
  onClose,
  title = "Configure Data Input",
  initialName,
  initialVisible,
  initialColor,
  initialShape,
  hasGroupsOrClusters,
}: {
  onClose: () => void;
  title?: string;
  initialName?: string;
  initialVisible?: boolean;
  initialColor?: string;
  initialShape?: GroupShape;
  hasGroupsOrClusters?: boolean;
}) {
  const { currentLanguage } = useTranslation();
  const activeDataflowId = useActiveDataflowId();
  const activeDataflow = useActiveDataflow();
  const userFiles = useUserFiles();
  const { getBookmarkFromTableId, bookmarks } = useBookmarksContext();
  const updateTransformer = useDataflowStore(
    (state) => state.updateTransformer
  );
  const updateGeoJSONLayerVisibility = useMapStore(
    (state) => state.updateGeoJSONLayerVisibility
  );
  const geoJSONLayers = useMapStore((state) => state.geoJSONLayers);
  const setLayerConfig = useMapStore((state) => state.setLayerConfig);

  const inputDataName = useMemo(() => {
    return getInputDataNameFromFlow(
      activeDataflow?.flow_definition_json,
      userFiles,
      getBookmarkFromTableId,
      currentLanguage
    );
  }, [
    activeDataflow?.flow_definition_json,
    userFiles,
    getBookmarkFromTableId,
    currentLanguage,
  ]);

  const currentInputTransformer = useMemo(() => {
    return activeDataflow?.flow_definition_json?.find(
      (t) => t.type === "data_input" || t.type === "user_file"
    );
  }, [activeDataflow?.flow_definition_json]);

  // Build options for data input selector
  const dataInputOptions = useMemo(() => {
    const groupedOptions: Array<{
      group: string;
      items: Array<{ value: string; label: string }>;
    }> = [];

    // Group user files
    const userFileItems: Array<{ value: string; label: string }> = [];
    userFiles.forEach((file: UserFile) => {
      userFileItems.push({
        value: `user_file:${file.id}`,
        label: file.name,
      });
    });
    if (userFileItems.length > 0) {
      groupedOptions.push({
        group: "My Data",
        items: userFileItems,
      });
    }

    // Group bookmarks
    const bookmarkItems: Array<{ value: string; label: string }> = [];
    bookmarks.forEach((bookmark: Bookmark) => {
      const name =
        bookmark.db_table.table_name_translation[currentLanguage] ||
        bookmark.db_table.table_name_english ||
        bookmark.db_table.table_name;
      bookmarkItems.push({
        value: `data_input:${bookmark.bookmark_id}`,
        label: name,
      });
    });
    if (bookmarkItems.length > 0) {
      groupedOptions.push({
        group: "Favorites",
        items: bookmarkItems,
      });
    }

    return groupedOptions;
  }, [userFiles, bookmarks, currentLanguage]);

  const currentValue = useMemo(() => {
    if (!currentInputTransformer) return null;
    if (currentInputTransformer.type === "user_file") {
      return `user_file:${(currentInputTransformer as any).file_id}`;
    } else if (currentInputTransformer.type === "data_input") {
      return `data_input:${(currentInputTransformer as any).db_table_id}`;
    }
    return null;
  }, [currentInputTransformer]);

  const isVisible = useMemo(() => {
    if (!activeDataflowId) return true;
    const geoJSONLayerId = `${PROCESSED_LAYER_PREFIX}${activeDataflowId}`;
    const geoJSONLayer = geoJSONLayers[geoJSONLayerId];
    return geoJSONLayer?.isVisible ?? true;
  }, [activeDataflowId, geoJSONLayers]);

  const form = useForm<{
    name: string;
    visible: boolean;
    dataInput: string | null;
    color?: string;
    shape?: GroupShape;
  }>({
    initialValues: {
      name: initialName || inputDataName || "",
      visible: initialVisible !== undefined ? initialVisible : isVisible,
      dataInput: currentValue,
      color: initialColor || "#003399",
      shape: initialShape || "circle",
    },
    validate: {
      name: (value) =>
        !value || value.trim() === "" ? "Name is required" : null,
    },
  });

  const handleSubmit = (values: {
    name: string;
    visible: boolean;
    dataInput: string | null;
    color?: string;
    shape?: GroupShape;
  }) => {
    if (!activeDataflowId) {
      logger.error("Cannot save data config: missing activeDataflowId");
      return;
    }

    // Update visibility
    const geoJSONLayerId = `${PROCESSED_LAYER_PREFIX}${activeDataflowId}`;
    updateGeoJSONLayerVisibility(geoJSONLayerId, values.visible);

    // Update layer config if no groups/clusters are set
    if (!hasGroupsOrClusters && values.color && values.shape) {
      setLayerConfig(activeDataflowId, {
        color: values.color,
        shape: values.shape,
      });
    }

    // Update data input if changed
    if (values.dataInput && values.dataInput !== currentValue) {
      const [type, id] = values.dataInput.split(":");
      const existingInputIndex =
        activeDataflow?.flow_definition_json?.findIndex(
          (item) => item.type === "data_input" || item.type === "user_file"
        );

      if (existingInputIndex !== undefined && existingInputIndex !== -1) {
        if (type === "user_file") {
          const userFile = userFiles.find((f) => f.id === id);
          if (userFile) {
            updateTransformer(activeDataflowId, existingInputIndex, {
              type: "user_file",
              file_id: userFile.id,
              file_name: userFile.name,
              file_type: userFile.type,
            });
          }
        } else if (type === "data_input") {
          const bookmark = getBookmarkFromTableId(id);
          if (bookmark) {
            updateTransformer(activeDataflowId, existingInputIndex, {
              type: "data_input",
              source: "public",
              db_table_id: bookmark.bookmark_id,
            });
          }
        }
      }
    }

    // Note: Custom name storage would need to be implemented separately
    // as there's no current mechanism to store custom names for data inputs

    onClose();
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={title}
      size="md"
      closeOnClickOutside={false}
      centered
    >
      <form
        onSubmit={form.onSubmit(handleSubmit, (errors) => {
          logger.error("Form validation errors:", errors);
        })}
      >
        <Stack gap="md">
          <TextInput
            label="Data Input Name"
            placeholder="Enter data input name"
            {...form.getInputProps("name")}
            required
            error={form.errors.name}
            readOnly
            description="Name is derived from the selected data input"
          />

          <Select
            label="Data Input"
            placeholder="Select data input"
            data={dataInputOptions}
            {...form.getInputProps("dataInput")}
            searchable
            allowDeselect={false}
          />

          <Checkbox
            label="Visible"
            {...form.getInputProps("visible", { type: "checkbox" })}
          />

          {!hasGroupsOrClusters && (
            <>
              <ColorInput
                label="Layer Color"
                format="hex"
                swatches={groupColors}
                {...form.getInputProps("color")}
                required
              />

              <div>
                <Text size="sm" fw={500} mb="xs">
                  Shape
                </Text>
                <SegmentedControl
                  fullWidth
                  data={[
                    {
                      value: "circle",
                      label: (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                          <GroupShapeIcon
                            shape="circle"
                            color={form.values.color || "#003399"}
                            size={20}
                          />
                        </div>
                      ),
                    },
                    {
                      value: "square",
                      label: (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                          <GroupShapeIcon
                            shape="square"
                            color={form.values.color || "#003399"}
                            size={20}
                          />
                        </div>
                      ),
                    },
                    {
                      value: "triangle",
                      label: (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                          <GroupShapeIcon
                            shape="triangle"
                            color={form.values.color || "#003399"}
                            size={20}
                          />
                        </div>
                      ),
                    },
                    {
                      value: "star",
                      label: (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                          <GroupShapeIcon
                            shape="star"
                            color={form.values.color || "#003399"}
                            size={20}
                          />
                        </div>
                      ),
                    },
                    {
                      value: "diamond",
                      label: (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                          <GroupShapeIcon
                            shape="diamond"
                            color={form.values.color || "#003399"}
                            size={20}
                          />
                        </div>
                      ),
                    },
                  ]}
                  {...form.getInputProps("shape")}
                />
              </div>
            </>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
